<template>
  <div v-if="visible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="fa-solid fa-trophy"></i>
          Bilhetes Premiados
        </h2>
        <button class="close-button" @click="closeModal">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Loading -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Carregando bilhetes premiados...</span>
          </div>
        </div>

        <!-- Estado vazio -->
        <div v-else-if="!winningTickets || winningTickets.length === 0" class="empty-state">
          <i class="fa-solid fa-trophy"></i>
          <h3>Nenhum bilhete premiado</h3>
          <p>Não foram encontrados bilhetes premiados para este resultado.</p>
        </div>

        <!-- Lista de bilhetes premiados -->
        <div v-else class="winning-tickets-list">
          <!-- Resumo -->
          <div class="summary-card">
            <div class="summary-item">
              <span class="summary-label">Total de Bilhetes:</span>
              <span class="summary-value">{{ winningTickets.length }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Valor Total dos Prêmios:</span>
              <span class="summary-value prize-value">R$ {{ totalPrizeValue.toFixed(2) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Valor Total das Apostas:</span>
              <span class="summary-value">R$ {{ totalBetValue.toFixed(2) }}</span>
            </div>
          </div>

          <!-- Tabela de Bilhetes Premiados -->
          <div class="table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>NSU</th>
                  <th>Data/Hora</th>
                  <th>Vendedor</th>
                  <th>Valor Aposta</th>
                  <th>Prêmio</th>
                  <th>Lucro</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="ticket in winningTickets" 
                  :key="ticket.betNsu"
                  class="data-row"
                >
                  <td>
                    <div class="nsu-info">
                      <span class="nsu-code">{{ ticket.betNsu }}</span>
                    </div>
                  </td>
                  
                  <td>
                    <div class="datetime-info">
                      <span class="date">{{ formatDate(ticket.betDateTime) }}</span>
                      <span class="time">{{ formatTime(ticket.betDateTime) }}</span>
                    </div>
                  </td>
                  
                  <td>
                    <div class="seller-info">
                      <i class="fa-solid fa-user"></i>
                      <span>{{ ticket.sellerName }}</span>
                    </div>
                  </td>
                  
                  <td>
                    <span class="currency-value">R$ {{ ticket.betAmount.toFixed(2) }}</span>
                  </td>
                  
                  <td>
                    <span class="currency-value currency-positive">R$ {{ ticket.totalPrize.toFixed(2) }}</span>
                  </td>
                  
                  <td>
                    <span 
                      class="currency-value" 
                      :class="{ 
                        'currency-positive': ticket.totalPrize > ticket.betAmount, 
                        'currency-negative': ticket.totalPrize <= ticket.betAmount 
                      }"
                    >
                      R$ {{ (ticket.totalPrize - ticket.betAmount).toFixed(2) }}
                    </span>
                  </td>
                  
                  <td>
                    <div class="action-buttons">
                      <button 
                        class="btn-action btn-view" 
                        @click="viewTicketDetails(ticket)"
                        title="Ver detalhes completos"
                      >
                        <i class="fa-solid fa-eye"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Detalhes do Bilhete -->
  <div v-if="showDetailsModal" class="modal-overlay" @click="closeDetailsModal">
    <div class="modal-content details-modal" @click.stop>
      <div class="modal-header">
        <h3>
          <i class="fa-solid fa-ticket"></i>
          Detalhes do Bilhete Premiado
        </h3>
        <button class="btn-close" @click="closeDetailsModal">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div v-if="selectedTicket" class="ticket-details">
          <!-- Informações Gerais -->
          <div class="detail-section">
            <h4><i class="fa-solid fa-info-circle"></i> Informações Gerais</h4>
            <div class="detail-grid">
              <div class="detail-group">
                <label>NSU:</label>
                <span>{{ selectedTicket.betNsu }}</span>
              </div>
              <div class="detail-group">
                <label>Vendedor:</label>
                <span>{{ selectedTicket.sellerName }}</span>
              </div>
              <div class="detail-group">
                <label>Data/Hora:</label>
                <span>{{ formatDateTime(selectedTicket.betDateTime) }}</span>
              </div>
              <div class="detail-group">
                <label>Valor da Aposta:</label>
                <span class="currency-value">R$ {{ selectedTicket.betAmount.toFixed(2) }}</span>
              </div>
              <div class="detail-group">
                <label>Total de Prêmios:</label>
                <span class="currency-value currency-positive">R$ {{ selectedTicket.totalPrize.toFixed(2) }}</span>
              </div>
              <div class="detail-group">
                <label>Lucro:</label>
                <span 
                  class="currency-value" 
                  :class="{ 
                    'currency-positive': selectedTicket.totalPrize > selectedTicket.betAmount, 
                    'currency-negative': selectedTicket.totalPrize <= selectedTicket.betAmount 
                  }"
                >
                  R$ {{ (selectedTicket.totalPrize - selectedTicket.betAmount).toFixed(2) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Todos os Números Apostados -->
          <div class="detail-section">
            <h4><i class="fa-solid fa-list"></i> Todos os Números Apostados</h4>
            <div class="numbers-list">
              <div 
                v-for="number in selectedTicket.allBetNumbers" 
                :key="`${number.number}-${number.modalityType}`" 
                class="number-row"
                :class="{ 'winning-number': number.isWinning, 'losing-number': !number.isWinning }"
              >
                <div class="number-info">
                  <span class="number" :class="{ 'highlighted': number.isWinning }">
                    {{ number.number }}
                    <i v-if="number.isWinning" class="fa-solid fa-crown winner-icon"></i>
                  </span>
                  <span class="modality">{{ number.modalityName }}</span>
                  <span class="placing">{{ number.modalityPlaceDescription }}</span>
                </div>
                
                <div class="number-details">
                  <div class="bet-calculation">
                    <span class="label">Valor Apostado:</span>
                    <span class="value">
                      R$ {{ number.betValue.toFixed(2) }}
                    </span>
                  </div>
                  
                  <div class="result-info">
                    <span v-if="number.isWinning" class="prize-info">
                      <i class="fa-solid fa-trophy"></i>
                      Prêmio: R$ {{ number.prizeValue.toFixed(2) }}
                    </span>
                    <span v-else class="no-prize">
                      <i class="fa-solid fa-times"></i>
                      Não premiado
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Detalhes dos Prêmios -->
          <div v-if="selectedTicket.winningNumbers && selectedTicket.winningNumbers.length > 0" class="detail-section">
            <h4><i class="fa-solid fa-star"></i> Detalhes dos Prêmios</h4>
            <div class="prize-details">
              <div 
                v-for="number in selectedTicket.winningNumbers" 
                :key="`prize-${number.number}-${number.modalityType}`" 
                class="prize-row"
              >
                <div class="prize-number">
                  <span class="number highlighted">{{ number.number }}</span>
                  <span class="modality">{{ number.modalityName }} - {{ number.modalityType }}</span>
                </div>
                <div class="prize-info-detail">
                  <span class="place">{{ number.resultPlace }}º lugar</span>
                  <span class="result-number">Sorteio: {{ number.resultNumber }}</span>
                  <span class="prize-value">R$ {{ number.prizeValue.toFixed(2) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";

export default {
  name: 'WinningTicketsModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    lotteryResultId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      winningTickets: [],
      showDetailsModal: false,
      selectedTicket: null,
      currentRegion: null
    }
  },
  computed: {
    totalPrizeValue() {
      return this.winningTickets.reduce((total, ticket) => total + ticket.totalPrize, 0);
    },
    totalBetValue() {
      return this.winningTickets.reduce((total, ticket) => total + ticket.betAmount, 0);
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.lotteryResultId) {
        this.loadCurrentRegion();
        this.loadWinningTickets();
      }
    }
  },
  methods: {
    loadCurrentRegion() {
      try {
        const regionData = localStorage.getItem('currentRegion');
        if (regionData) {
          this.currentRegion = JSON.parse(regionData);
        }
      } catch (error) {
        console.error('Erro ao carregar dados da região:', error);
        this.currentRegion = null;
      }
    },
    async loadWinningTickets() {
      this.loading = true;
      try {
        // Passar regionId se disponível
        const regionId = this.currentRegion?.id || null;
        const response = await mainService.getWinningTickets(this.lotteryResultId, regionId);
        this.winningTickets = response.data;
      } catch (error) {
        console.error('Erro ao carregar bilhetes premiados:', error);
        toast.error(handleErrorMessage(error));
        this.winningTickets = [];
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.$emit('close');
    },
    viewTicketDetails(ticket) {
      this.selectedTicket = ticket;
      this.showDetailsModal = true;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedTicket = null;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    formatDateTime(dateTime) {
      return jsFunctions.setDateTimeDisplay(dateTime);
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 12px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-content.details-modal {
  max-width: 1000px;
}

.modal-header {
  background: #fbfcff12;
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
}

.close-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.modal-body {
  padding: 1.5rem;
  max-height: calc(90vh - 80px);
  overflow-y: auto;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-muted);
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-muted);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: var(--accent-primary);
}

.summary-card {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-around;
  gap: 2rem;
  border: 1px solid var(--border-color);
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-muted);
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.prize-value {
  color: var(--success-color);
}

.loss-value {
  color: #dc3545;
}

.place, .result-number {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.winner-detail {
  border-left: 4px solid var(--success-color);
  background: rgba(40, 167, 69, 0.1);
}

.ticket-card {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 4px solid var(--accent-primary);
}

.ticket-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.ticket-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.ticket-nsu, .ticket-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-muted);
}

.ticket-seller {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
  font-size: 1rem;
}

.numbers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.number-card {
  background: var(--bg-primary);
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.number-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.number {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--accent-primary);
}

.highlighted {
  color: var(--success-color);
}

.winner-icon {
  color: var(--success-color);
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.modality, .modality-type {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.prize-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: right;
}

.prize-label {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.prize-value {
  font-weight: 600;
  color: var(--success-color);
}

.no-prize {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.no-prize-text {
  color: var(--text-muted);
}

.bet-summary {
  background: var(--bg-primary);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid var(--border-color);
  display: flex;
  justify-content: space-around;
  gap: 1rem;
}

.bet-summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.bet-calculation {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-muted);
}

.calculation-label {
  font-size: 0.75rem;
}

.calculation-value {
  font-weight: 600;
  color: var(--text-primary);
}

.all-numbers-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}

.all-numbers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.bet-number-card {
  background: var(--bg-primary);
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bet-number-card.winning-number {
  border-left: 4px solid var(--success-color);
}

.bet-number-card.losing-number {
  border-left: 4px solid #dc3545;
  opacity: 0.6;
}

.bet-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: right;
}

.bet-calculation {
  text-align: left;
}

/* Tabela e nova estrutura */
.table-container {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-primary);
}

.modern-table th {
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid var(--border-color);
}

.modern-table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
}

.data-row:hover {
  background: var(--hover-color);
}

.nsu-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nsu-code {
  font-family: monospace;
  font-weight: 600;
  color: var(--text-primary);
}

.datetime-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date {
  font-weight: 500;
  color: var(--text-primary);
}

.time {
  font-size: 0.875rem;
  color: var(--text-muted);
}

.seller-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
}

.seller-info i {
  color: var(--accent-primary);
}

.currency-value {
  font-weight: 600;
  color: var(--text-primary);
}

.currency-positive {
  color: #28a745;
}

.currency-negative {
  color: #dc3545;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-view {
  background: var(--accent-primary);
  color: white;
}

.btn-view:hover {
  background: var(--accent-primary-dark);
  transform: translateY(-1px);
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: white;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Modal de Detalhes */
.ticket-details {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.detail-section {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
}

.detail-section h4 {
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
}

.detail-section h4 i {
  color: var(--accent-primary);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.detail-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.detail-group label {
  font-weight: 500;
  color: var(--text-muted);
}

.detail-group span {
  font-weight: 600;
  color: var(--text-primary);
}

/* Lista de números */
.numbers-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.number-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 6px;
  border: 1px solid var(--border-color);
  transition: all 0.2s;
}

.number-row.winning-number {
  border-left: 4px solid #28a745;
  background: rgba(40, 167, 69, 0.05);
}

.number-row.losing-number {
  border-left: 4px solid #dc3545;
  opacity: 0.7;
}

.number-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.number {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
}

.number.highlighted {
  color: #28a745;
}

.winner-icon {
  color: #ffd700;
  font-size: 0.875rem;
  margin-left: 0.5rem;
}

.modality, .placing {
  font-size: 0.875rem;
  color: var(--text-muted);
}

.number-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: right;
}

.bet-calculation {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.bet-calculation .label {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.bet-calculation .value {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
}

.result-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.prize-info {
  color: #28a745;
  font-weight: 600;
  font-size: 0.875rem;
}

.prize-info i {
  color: #ffd700;
}

.no-prize {
  color: var(--text-muted);
  font-size: 0.875rem;
}

.no-prize i {
  color: #dc3545;
}

/* Detalhes dos prêmios */
.prize-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.prize-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 6px;
  border: 1px solid var(--border-color);
  border-left: 4px solid #ffd700;
}

.prize-number {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.prize-number .number {
  font-size: 1.125rem;
  font-weight: 700;
  color: #28a745;
}

.prize-number .modality {
  font-size: 0.875rem;
  color: var(--text-muted);
}

.prize-info-detail {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: right;
}

.place, .result-number {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.prize-value {
  font-weight: 600;
  color: #28a745;
  font-size: 1rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 95vh;
  }
  
  .summary-card {
    flex-direction: column;
    gap: 1rem;
  }
  
  .modern-table {
    font-size: 0.875rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 0.5rem;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
  
  .number-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .number-details {
    text-align: left;
    width: 100%;
  }
  
  .prize-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .prize-info-detail {
    text-align: left;
    width: 100%;
  }
}
</style> 